import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

interface ErrorResponseData {
  detail: string;
}

interface FailedRequest {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

class ApiClient {
  private readonly apiClient: AxiosInstance;
  private isRefreshing: boolean;
  private failedQueue: FailedRequest[];

  constructor(baseURL: string) {
    this.apiClient = axios.create({
      baseURL: baseURL,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });

    this.isRefreshing = false;
    this.failedQueue = [];

    this.apiClient.interceptors.request.use(this.addAuthHeader);
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => this.handleTokenExpiration(error)
    );
    this.apiClient.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 0 && error.message?.includes('CORS')) {
          console.error('CORS Error:', error);
          // Handle CORS error - could redirect to error page or retry
        }
        return Promise.reject(error);
      }
    );
  }

  // Add Authorization header to every request
  private readonly addAuthHeader = (
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig => {
    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  };

  // Handle refresh token logic
  private readonly handleTokenExpiration = async (
    error: AxiosError
  ): Promise<AxiosResponse | void> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      const responseData = error.response.data as ErrorResponseData;
      if (responseData?.detail === "Token has expired") {
        if (this.isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.apiClient(originalRequest);
            })
            .catch((err) =>
              Promise.reject(new Error(err?.message || "Request failed"))
            );
        }

        originalRequest._retry = true;
        this.isRefreshing = true;

        const refreshToken = localStorage.getItem("refresh_token");

        return new Promise<AxiosResponse>((resolve, reject) => {
          axios
            .post(`${this.apiClient.defaults.baseURL}/auth/refresh-token`, {
              refresh_token: refreshToken,
            })
            .then(({ data }) => {
              const newAccessToken = data.access_token;
              localStorage.setItem("id_token", newAccessToken);
              this.apiClient.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${newAccessToken}`;
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              this.processQueue(null, newAccessToken);
              resolve(this.apiClient(originalRequest));
            })
            .catch((err) => {
              this.processQueue(err, null);
              localStorage.removeItem("id_token");
              localStorage.removeItem("refresh_token");
              window.location.href = "/login";
              reject(new Error(err?.message || "Token refresh failed"));
            })
            .finally(() => {
              this.isRefreshing = false;
            });
        });
      }
    }

    return Promise.reject(new Error(error?.message || "Request failed"));
  };

  // Process the queue of failed requests during token refresh
  private readonly processQueue = (
    error: AxiosError | null,
    token: string | null = null
  ): void => {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(new Error(error?.message || "Request failed"));
      } else if (token) {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  };

  // Expose apiClient instance
  public getClient(): AxiosInstance {
    return this.apiClient;
  }
}

export default ApiClient;
